import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FORMAT_DATE_TIME } from '../../../const';
import { RootState } from '../../../store';
import { setterGant } from '../../../store/slices/gantSlice';
import { setterProjectTree } from '../../../store/slices/projectTreeSlice';
import { ItemType, MappingItemIdIndexType, ScheduleItemType } from '../../../types';
import { findLastExpandedChild, removeRowBorder, removeRowClass, setRowBorder, setRowClas } from '../projectTreeUtil';
import { useRowsMap } from './useRowsMap';

export const AppendItem = {
    CSS_CLASS: {
        appendMarker: 'project-tree-append-item-marker',
        borderTopBottom: 'project-tree-append-border-top-bottom',
        borderLeft: 'project-tree-append-border-left',
        borderRight: 'project-tree-append-border-right',
    },
};

export const useAppendChildItem = (items: ItemType[], mappingItemIdIndex: MappingItemIdIndexType) => {
    const dispatch = useDispatch();

    const appendRequestingItem = useSelector<RootState, ItemType | null>(
        (state) => state.projectTree.appendRequestingItem,
    );

    const appendIntoItem = useSelector<RootState, ItemType | null>((state) => state.projectTree.appendIntoItem);

    const previousHighlightItemId = useRef<string | null>();

    const { rowsMap } = useRowsMap();

    const findPhaseId = (item: ItemType): number | undefined => {
        if (item.phaseId) {
            return item.phaseId;
        }
        if (!item.parentId) {
            return undefined;
        }
        const parent = items[mappingItemIdIndex[item.parentId]];
        return parent ? findPhaseId(parent) : undefined;
    };

    useEffect(() => {
        if (previousHighlightItemId.current) {
            removeRowClass(rowsMap, previousHighlightItemId.current, AppendItem.CSS_CLASS.appendMarker);
            removeRowBorder(
                previousHighlightItemId.current,
                rowsMap,
                AppendItem.CSS_CLASS.borderLeft,
                AppendItem.CSS_CLASS.borderRight,
                AppendItem.CSS_CLASS.borderTopBottom,
            );
        }

        if (appendRequestingItem) {
            const highlightId = findLastExpandedChild(appendRequestingItem.id, items, mappingItemIdIndex);

            if (highlightId === appendRequestingItem.id) {
                setRowBorder(
                    highlightId,
                    rowsMap,
                    AppendItem.CSS_CLASS.borderLeft,
                    AppendItem.CSS_CLASS.borderRight,
                    AppendItem.CSS_CLASS.borderTopBottom,
                );
            } else {
                setRowClas(rowsMap, highlightId, AppendItem.CSS_CLASS.appendMarker);
            }

            previousHighlightItemId.current = highlightId;
        } else {
            previousHighlightItemId.current = null;
        }
    }, [appendRequestingItem, rowsMap, items, mappingItemIdIndex]);

    useEffect(() => {
        if (appendIntoItem) {
            const newItem = {
                color: appendIntoItem.color,
                start_datetime: DateTime.fromISO(appendIntoItem.startDate).toFormat(FORMAT_DATE_TIME),
                end_datetime: DateTime.fromISO(appendIntoItem.startDate).plus({ day: 4 }).toFormat(FORMAT_DATE_TIME),
                project_id: appendIntoItem.projectId,
                phase_id: findPhaseId(appendIntoItem),
                parent_id: appendIntoItem.id,
                completion_percentage: 0,
                assignee: '',
                role_item: 'workItem',
            } as ScheduleItemType;

            dispatch(
                setterProjectTree({
                    appendIntoItem: null,
                }),
            );

            dispatch(setterGant({ appendExternalItem: newItem }));
        }
    }, [appendIntoItem]);
};
